// 261010 Epic 2 / P2b-3 — bước 1 KỊCH BẢN của một video, CÓ sửa tay: action, số giây, độ nặng kịch, các dòng thoại (người nói / mode / chữ, thêm / xoá).
// Ghi bằng đúng đường của UI cũ (`batchEditPlan`: updateEvent + replaceDialogue) qua `WorkflowEditFrame` — không AI, không tự đồng bộ. Logic dựng ops ở `workflowEditOps.ts` (thuần, có test).
import React from 'react';
import { Button, Input, InputNumber, Select } from 'antd';
import { PlusOutlined } from '@ant-design/icons';
import type { EventPlan, FilmEvent } from '../../types/film';
import EventDialogueRow from './EventDialogueRow';
import WorkflowEditFrame from './WorkflowEditShared';
import type { InteractiveProps } from './WorkflowStepShared';
import WorkflowEventStructBar from './WorkflowEventStructBar';
import { DRAMATIC_WEIGHTS, lineTakenOf, moveBeatWithLines, newLine, setBeatText } from './workflowEditOps';
import { formatClock } from './workflowBoardUtils';
import { beatLines, dialogueLine } from './workflowDetailUtils';

interface Props extends InteractiveProps {
  eventId: string;
  ev: FilmEvent;
  detail: EventPlan | null;
}

const WEIGHT_LABEL: Record<string, string> = { PEAK: '🔥 PEAK', NORMAL: 'NORMAL', TRANSITION: '→ TRANSITION' };

/** Chế độ xem — giữ nguyên như P2a. */
export const ScriptView: React.FC<{ ev: FilmEvent }> = ({ ev }) => {
  const phases = ev.videoMeta?.phases ?? [];
  const beats = beatLines(ev.beatMeta);
  const lines = (ev.dialogueLines || []).map(dialogueLine).filter(Boolean);
  return (
    <div>
      <h3>Hành động</h3>
      <p style={{ margin: 0, overflowWrap: 'anywhere' }}>{ev.action || <span className="muted">Chưa có mô tả hành động.</span>}</p>
      <div className="wf-kv"><span>Thời lượng: <b className="mono">{formatClock(ev.genDurationS)}</b></span><span>{phases.length} giai đoạn</span><span>{lines.length} thoại</span></div>
      {phases.length > 0 && (<><h3>Các giai đoạn</h3><ul className="wf-list">{phases.map((p) => <li key={p.order}><span className="k">GĐ{p.order}</span>{p.purpose}{p.endState ? <span className="muted"> — kết: {p.endState}</span> : null}</li>)}</ul></>)}
      {beats.length > 0 && (<><h3>Nhịp hành động</h3><ul className="wf-list">{beats.map((b) => <li key={b}>{b}</li>)}</ul></>)}
      {lines.length > 0 && (<><h3>Thoại</h3><ul className="wf-list">{lines.map((l, i) => <li key={i}>{l}</li>)}</ul></>)}
    </div>
  );
};

const WorkflowScriptStep: React.FC<Props> = (props) => {
  const { ev, detail } = props;
  const maxS = Math.max(ev.videoEngine === 'SEEDANCE_25' ? 30 : 15, ev.genDurationS ?? 0);
  return (
    <WorkflowEditFrame
      {...props} stepKey="SCRIPT"
      extra={({ editing }) => <WorkflowEventStructBar eventId={props.eventId} ev={ev} detail={detail} editing={editing} onActionDone={props.onActionDone} />}
      view={(e) => <ScriptView ev={e} />}
      renderEditor={({ draft, update, bank, saving }) => {
        const names = Array.from(new Set([...(bank?.characters || detail?.refBank?.characters || []).map((c) => c.name), ...draft.lines.map((l) => l.speakerName).filter(Boolean) as string[]]));
        const castOpts = names.map((n) => ({ label: n, value: n }));
        const taken = lineTakenOf(draft.shots);
        return (
          <div>
            <h3>Hành động</h3>
            <Input.TextArea autoSize={{ minRows: 2, maxRows: 10 }} value={draft.action ?? ''} disabled={saving} aria-label="Hành động của video"
              onChange={(e) => { const v = e.target.value; update((d) => { d.action = v; }); }} data-testid="wf-script-action" />
            <p className="wf-note" style={{ margin: '4px 0 8px' }}>Đây là nguồn AI dùng để dựng prompt — lưu xong prompt, clip và liền mạch các video sau sẽ thành cũ (chỉ hiện, không tự dựng lại).</p>
            <div className="wf-kv" style={{ alignItems: 'center' }}>
              <span>Số giây:{' '}
                <InputNumber size="small" min={1} max={maxS} step={1} precision={0} value={draft.genDurationS ?? undefined} disabled={saving} addonAfter="s" aria-label="Số giây của video"
                  onChange={(v) => { if (typeof v === 'number' && v > 0) update((d) => { d.genDurationS = v; }); }} />
              </span>
              <span>Độ nặng kịch:{' '}
                <Select size="small" style={{ width: 140 }} value={draft.dramaticWeight} disabled={saving} aria-label="Độ nặng kịch"
                  options={DRAMATIC_WEIGHTS.map((w) => ({ value: w, label: WEIGHT_LABEL[w] }))}
                  onChange={(v) => update((d) => { d.dramaticWeight = v; })} />
              </span>
            </div>
            <div className="wf-kv" style={{ alignItems: 'center', marginTop: 6 }} data-testid="wf-script-beatmeta">
              <span>Chiến thuật:{' '}
                <Input size="small" style={{ width: 170 }} value={draft.tactic ?? ''} disabled={saving} aria-label="Chiến thuật của video" maxLength={120}
                  onChange={(e) => { const v = e.target.value; update((d) => { d.tactic = v.trim() ? v : null; }); }} />
              </span>
              <span>Người khởi xướng:{' '}
                <Input size="small" style={{ width: 150 }} value={draft.initiator ?? ''} disabled={saving} aria-label="Người khởi xướng" maxLength={80}
                  onChange={(e) => { const v = e.target.value; update((d) => { d.initiator = v.trim() ? v : null; }); }} />
              </span>
              <span>Sự kiện cảm xúc:{' '}
                <Input size="small" style={{ width: 220 }} value={draft.emotionalEvent ?? ''} disabled={saving} aria-label="Sự kiện cảm xúc" maxLength={160}
                  onChange={(e) => { const v = e.target.value; update((d) => { d.emotionalEvent = v.trim() ? v : null; }); }} />
              </span>
            </div>
            {draft.actionBeats.length > 0 && (
              <div data-testid="wf-script-beats">
                <h3>Nhịp hành động</h3>
                {draft.actionBeats.map((b, i) => (
                  <div key={`${b.order}-${i}`} style={{ display: 'flex', gap: 6, alignItems: 'center', margin: '3px 0' }}>
                    <span className="mono faint" style={{ width: 22 }}>{i + 1}.</span>
                    <Input size="small" value={b.text} disabled={saving} aria-label={`Nhịp ${i + 1}`} onChange={(e) => { const v = e.target.value; update((d) => { d.actionBeats = setBeatText(d.actionBeats, i, v); }); }} />
                    <Button size="small" disabled={saving || i === 0} aria-label={`Đưa nhịp ${i + 1} lên`} onClick={() => update((d) => { const r = moveBeatWithLines(d.actionBeats, d.lines, i, -1); d.actionBeats = r.beats; d.lines = r.lines; })}>↑</Button>
                    <Button size="small" disabled={saving || i === draft.actionBeats.length - 1} aria-label={`Đưa nhịp ${i + 1} xuống`} onClick={() => update((d) => { const r = moveBeatWithLines(d.actionBeats, d.lines, i, 1); d.actionBeats = r.beats; d.lines = r.lines; })}>↓</Button>
                  </div>
                ))}
                <p className="wf-note" style={{ margin: '4px 0 0' }}>Đổi nhịp là đổi hash Phân đoạn: bản đã duyệt thành cũ, phải duyệt lại. Thoại đi theo nhịp mà nó neo (neo được đổi số tương ứng khi đổi chỗ).</p>
              </div>
            )}
            <p className="wf-note" style={{ margin: '4px 0 0' }}>Số giây được hệ thống tự tính lại từ nhịp và thoại mỗi lần lưu thay đổi khác; số bạn nhập chỉ được giữ khi bạn đổi số giây ngay trong lượt lưu đó.</p>
            <h3>Thoại</h3>
            {draft.lines.length === 0 && <p className="muted" style={{ margin: '4px 0' }}>Chưa có dòng thoại.</p>}
            {draft.lines.map((d, i) => (
              <EventDialogueRow key={d.id} d={d} castOpts={castOpts} shotOrder={taken.get(i + 1)?.order}
                onMode={(v) => update((x) => { x.lines[i].dialogueMode = v; })}
                onSpeaker={(v) => update((x) => { x.lines[i].speakerName = v; })}
                onText={(v) => update((x) => { x.lines[i].textEn = v; })}
                onDelete={() => update((x) => { x.lines.splice(i, 1); })} />
            ))}
            <Button size="small" type="dashed" icon={<PlusOutlined />} disabled={saving} style={{ marginTop: 4 }} onClick={() => update((x) => { x.lines.push(newLine()); })}>Thêm dòng thoại</Button>
          </div>
        );
      }}
    />
  );
};

export default WorkflowScriptStep;
